import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertBox, Breadcrumb, Icon, SectionHeader, Text } from "@zorni/components-lib";
import styles from "./LegalPage.module.css";

/* Bandeau affiché tant que le texte n'a pas été validé juridiquement. */
const DraftNotice = ({ t }) => (
  <div className={styles.notice}>
    <AlertBox
      type="warning"
      title={t("legal.draftTitle", "Texte provisoire")}
      message={t(
        "legal.draftNotice",
        "En attente de validation juridique. Les passages entre crochets restent à compléter."
      )}
    />
  </div>
);

const PageBreadcrumb = ({ t, title }) => (
  <Breadcrumb
    className={styles.breadcrumb}
    ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
    linkComponent={Link}
    items={[{ label: t("trips.results.home", "Accueil"), to: "/" }, { label: title }]}
  />
);

const Block = ({ block }) => {
  if (block.type === "ul") {
    return (
      <ul className={styles.list}>
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "table") {
    return (
      <table className={styles.table}>
        <tbody>
          <tr>
            {block.head.map((cell) => (
              <th key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
          {block.rows.map((row) => (
            <tr key={row.join("|")}>
              {row.map((cell, index) => (
                <td key={`${row[0]}-${index}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return <p>{block.text}</p>;
};

/**
 * Page annexe en deux colonnes : sommaire ancré à gauche, sections à droite.
 * Reprend le gabarit `legal-layout` des maquettes.
 */
export const LegalPage = ({ content }) => {
  const { t } = useTranslation();
  const { title, kicker, subtitle, updatedAt, sections, isDraft } = content;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <PageBreadcrumb t={t} title={title} />

        <SectionHeader
          size="page"
          className={styles.pageHeader}
          kicker={kicker}
          title={title}
          subtitle={subtitle}
        />

        {updatedAt && (
          <p className={styles.updated}>
            {t("legal.updatedAt", "Dernière mise à jour")} : {updatedAt}
          </p>
        )}

        {isDraft && <DraftNotice t={t} />}

        <div className={styles.layout}>
          <aside className={styles.toc} aria-label={t("legal.toc", "Sommaire")}>
            <h2 className={styles.tocTitle}>{t("legal.toc", "Sommaire")}</h2>
            <ul className={styles.tocList}>
              {sections.map((section, index) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>
                    {String(index + 1).padStart(2, "0")}. {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className={styles.prose}>
            {sections.map((section, index) => (
              <section key={section.id} id={section.id}>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.num} aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {section.title}
                </h2>
                {section.blocks.map((block, blockIndex) => (
                  <Block key={`${section.id}-${blockIndex}`} block={block} />
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Question dépliable de la FAQ. */
const FaqItem = ({ item, id }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.accItem}>
      <button
        type="button"
        className={styles.accBtn}
        aria-expanded={isOpen}
        aria-controls={`${id}-panel`}
        id={`${id}-btn`}
        onClick={() => setIsOpen((open) => !open)}
      >
        {item.q}
        <span
          className={`${styles.chev} ${isOpen ? styles.chevOpen : ""}`}
          aria-hidden="true"
        >
          <Icon name="iconChevronDown" size="sm" />
        </span>
      </button>

      {isOpen && (
        <div
          className={styles.accPanel}
          id={`${id}-panel`}
          role="region"
          aria-labelledby={`${id}-btn`}
        >
          <Text size="sm" color="subtle">
            {item.a}
          </Text>
        </div>
      )}
    </div>
  );
};

/** FAQ : mêmes en-tête et conteneur, questions dépliables par groupe. */
export const FaqPage = ({ content }) => {
  const { t } = useTranslation();
  const { title, kicker, subtitle, groups, isDraft } = content;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <PageBreadcrumb t={t} title={title} />

        <SectionHeader
          size="page"
          className={styles.pageHeader}
          kicker={kicker}
          title={title}
          subtitle={subtitle}
        />

        {isDraft && <DraftNotice t={t} />}

        <div className={styles.layout}>
          <aside className={styles.toc} aria-label={t("legal.toc", "Sommaire")}>
            <h2 className={styles.tocTitle}>{t("legal.toc", "Sommaire")}</h2>
            <ul className={styles.tocList}>
              {groups.map((group) => (
                <li key={group.id}>
                  <a href={`#${group.id}`}>{group.title}</a>
                </li>
              ))}
            </ul>
          </aside>

          <div className={styles.prose}>
            {groups.map((group) => (
              <section key={group.id} id={group.id}>
                <h2 className={styles.groupTitle}>{group.title}</h2>
                {group.items.map((item, index) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    id={`${group.id}-${index}`}
                  />
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
